import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useI18n } from "../i18n";
import { ArrowRight, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

export default function CaseStudies() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/insights")
      .then((r) => setItems(r.data.filter((i) => i.category === "case-study")))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const t = lang === "it";

  return (
    <main data-testid="case-studies-page">
      <SEO
        title="Storie · Scenari illustrativi"
        description="Tre monologhi di imprenditori italiani: capire quanto vale davvero l'azienda, le conseguenze di una non-successione, monitorarsi anche senza vendere. Scenari illustrativi basati su situazioni tipiche delle PMI italiane."
        path="/case-studies"
      />
      {/* HERO */}
      <section style={{ background: "var(--card)" }} className="py-20 md:py-28">
        <div className="container-x">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-xs mb-8"
            style={{ opacity: 0.7 }}
            data-testid="back-home"
          >
            <ArrowLeft size={14} />
            {t ? "Torna alla home" : "Back to home"}
          </Link>
          <div className="overline" style={{ color: "var(--terracotta)" }}>
            {t ? "Storie · Scenari illustrativi" : "Stories · Illustrative scenarios"}
          </div>
          <h1
            className="serif-display mt-6"
            style={{ fontSize: "clamp(40px, 5.5vw, 72px)", maxWidth: 980 }}
          >
            {t ? (
              <>
                Tre imprenditori.
                <br />
                <span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>
                  Tre conversazioni.
                </span>
                <br />
                Un solo strumento.
              </>
            ) : (
              <>
                Three entrepreneurs.
                <br />
                <span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>
                  Three conversations.
                </span>
                <br />
                One tool.
              </>
            )}
          </h1>
          <p
            className="mt-8 text-lg md:text-xl"
            style={{ maxWidth: 720, opacity: 0.78, lineHeight: 1.7 }}
          >
            {t
              ? "Scenari illustrativi basati su situazioni tipiche delle PMI italiane. Non sono acquisizioni concluse: sono i tre modi ricorrenti in cui un imprenditore si affaccia a GIANO — vendere, capire cosa fare, monitorarsi."
              : "Illustrative scenarios based on typical situations of Italian SMEs. These are not completed acquisitions: they are the three recurring ways an entrepreneur approaches GIANO — selling, deciding what to do, monitoring."}
          </p>
        </div>
      </section>

      {/* LIST */}
      <section className="py-16 md:py-24" style={{ background: "var(--muted)" }}>
        <div className="container-x">
          {loading && (
            <div className="font-mono text-xs" style={{ opacity: 0.6 }}>
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="font-mono text-sm" style={{ opacity: 0.6 }}>
              {t ? "Nessuna storia pubblicata al momento." : "No stories published yet."}
            </div>
          )}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            data-testid="stories-grid"
          >
            {items.map((s, idx) => {
              const title = lang === "it" ? s.title_it : s.title_en;
              const excerpt = lang === "it" ? s.excerpt_it : s.excerpt_en;
              const num = String(idx + 1).padStart(2, "0");
              const isSvg = s.cover_url && s.cover_url.endsWith(".svg");
              return (
                <Link
                  key={s.id}
                  to={`/insights/${s.slug}`}
                  className="card-flat group"
                  style={{
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                    transition: "transform 0.3s ease",
                  }}
                  data-testid={`story-card-${s.slug}`}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <div
                    style={{
                      aspectRatio: "16/10",
                      overflow: "hidden",
                      background: isSvg ? "var(--bone)" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {s.cover_url && (
                      <img
                        src={s.cover_url}
                        alt={title}
                        style={{
                          width: isSvg ? "80%" : "100%",
                          height: isSvg ? "80%" : "100%",
                          objectFit: isSvg ? "contain" : "cover",
                          filter: "saturate(0.9)",
                        }}
                      />
                    )}
                  </div>
                  <div className="p-6 md:p-8">
                    <div
                      className="font-mono text-xs mb-4"
                      style={{ color: "var(--terracotta)", letterSpacing: "0.12em" }}
                    >
                      STORIA · {num}
                    </div>
                    <h3
                      className="serif-display"
                      style={{ fontSize: "clamp(22px, 2.2vw, 28px)", lineHeight: 1.2 }}
                    >
                      {title}
                    </h3>
                    <p
                      className="mt-4 text-sm md:text-base"
                      style={{ opacity: 0.7, lineHeight: 1.65 }}
                    >
                      {excerpt}
                    </p>
                    <div
                      className="mt-6 inline-flex items-center gap-2 font-mono text-xs"
                      style={{ color: "var(--midnight)" }}
                    >
                      {t ? "Leggi la storia" : "Read the story"}
                      <ArrowRight
                        size={14}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        style={{ background: "var(--midnight)", color: "var(--bone)" }}
        className="py-20 md:py-28"
      >
        <div className="container-x text-center">
          <div className="overline" style={{ opacity: 0.7 }}>
            {t ? "Anche la tua azienda?" : "Your company too?"}
          </div>
          <h2
            className="serif-display mt-4"
            style={{
              fontSize: "clamp(32px, 4vw, 48px)",
              maxWidth: 760,
              margin: "0 auto",
              lineHeight: 1.2,
            }}
          >
            {t
              ? "Se stai valutando il futuro della tua impresa, il primo passo è una conversazione riservata."
              : "If you are weighing the future of your business, the first step is a confidential conversation."}
          </h2>
          <Link
            to="/#contact"
            className="btn-primary mt-10 inline-flex"
            style={{
              background: "var(--bone)",
              color: "var(--midnight)",
              borderColor: "var(--bone)",
            }}
            data-testid="stories-cta-contact"
          >
            {t ? "Contattaci" : "Contact us"}
          </Link>
        </div>
      </section>
    </main>
  );
}
